import { ShieldOff } from "@untitledui/icons";
import { useState } from "react";
import { useLoaderData, useNavigate } from "react-router";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { CopyField } from "@/components/admin/copy-field";
import { FieldSkeleton, Loading, Skeleton } from "@/components/admin/loading";
import { useToast } from "@/components/admin/toast";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import GitHub from "@/components/foundations/social-icons/github";
import type { AdminMe, Api } from "../lib/api.js";

const page = "flex min-h-dvh items-center justify-center bg-secondary_subtle px-4 py-12";

export function SignInRoute() {
  const { api } = useLoaderData<{ api: Api }>();
  const toast = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    try {
      await work();
    } catch (err) {
      toast.error(err);
      setBusy(false);
    }
  };

  return (
    <main className={page}>
      <div className="flex w-full max-w-90 flex-col gap-6 rounded-2xl bg-primary p-8 shadow-lg ring-1 ring-secondary">
        <h1 className="text-display-xs font-semibold text-primary">Diotia Admin</h1>
        <form
          className="flex flex-col gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            const data = new FormData(e.currentTarget);
            void run(async () => (await api.signIn(String(data.get("email")), String(data.get("password"))), void (await navigate("/"))));
          }}
        >
          <Input label="电子邮件" name="email" type="email" autoComplete="username" isRequired hideRequiredIndicator isDisabled={busy} />
          <Input label="密码" name="password" type="password" autoComplete="current-password" isRequired hideRequiredIndicator isDisabled={busy} />
          <Button type="submit" size="lg" isLoading={busy} showTextWhileLoading>
            登录
          </Button>
        </form>
        <Button color="secondary" size="lg" isDisabled={busy} iconLeading={<GitHub data-icon size={20} />} onClick={() => void run(async () => window.location.assign((await api.signInWithGitHub(window.location.origin)).url))}>
          使用 GitHub 登录
        </Button>
      </div>
    </main>
  );
}

/** A fresh load of /no-access, while it finds out who is signed in: the same notice without its values. */
export function NoAccessSkeleton() {
  return (
    <main className={page}>
      <Loading>
        <div className="flex w-full max-w-88 flex-col items-center gap-4">
          <Skeleton className="size-12 rounded-full" />
          <Skeleton className="h-6 w-32" />
          <Skeleton className="w-44" />
          <div className="mt-2 w-full">
            <FieldSkeleton label="用户 ID" />
          </div>
          <Skeleton className="mt-4 h-10 w-24 rounded-lg" />
        </div>
      </Loading>
    </main>
  );
}

/** Signed in, and not an administrator. The id is what an administrator (or PLATFORM_ADMIN_USER_IDS) needs. */
export function NoAccessRoute() {
  const { api, me } = useLoaderData<{ api: Api; me: AdminMe }>();
  const navigate = useNavigate();
  return (
    <main className={page}>
      <EmptyState size="md">
        <EmptyState.Header pattern="none">
          <EmptyState.FeaturedIcon icon={ShieldOff} color="warning" theme="light" />
        </EmptyState.Header>
        <EmptyState.Content>
          <EmptyState.Title>不是平台管理员</EmptyState.Title>
          <EmptyState.Description>{me.email}</EmptyState.Description>
        </EmptyState.Content>
        <div className="mb-6 w-full max-w-88">
          <CopyField label="用户 ID" value={me.id} />
        </div>
        <EmptyState.Footer>
          <Button color="secondary" onClick={async () => (await api.signOut(), void (await navigate("/sign-in")))}>
            退出登录
          </Button>
        </EmptyState.Footer>
      </EmptyState>
    </main>
  );
}
